import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { FieldFeedback, FormError } from "../ui/feedback";
import { FlowpiAlertDialog } from "../ui/flowpi-dialog";

export default function QuotationLifecycleDialog({
  open,
  quotation,
  error = "",
  onClose,
  onConfirm,
}) {
  const [reason, setReason] = useState("");
  const [localError, setLocalError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const requestInFlight = useRef(false);

  useEffect(() => {
    if (!open) return;
    setReason("");
    setLocalError("");
    setSubmitting(false);
    requestInFlight.current = false;
  }, [open, quotation?.id]);

  if (!open || !quotation) return null;

  const close = () => {
    if (!requestInFlight.current) onClose?.();
  };

  const submit = async () => {
    if (requestInFlight.current) return;
    const normalizedReason = reason.trim();
    if (!normalizedReason) {
      setLocalError("Delete Reason is required.");
      return;
    }
    requestInFlight.current = true;
    setSubmitting(true);
    setLocalError("");
    try {
      await onConfirm?.(normalizedReason);
    } catch {
      // The parent supplies the normalized API error below.
    } finally {
      requestInFlight.current = false;
      setSubmitting(false);
    }
  };

  const footer = (
    <>
      <Button type="button" variant="cancel" disabled={submitting} onClick={close} data-action-category="cancel">Cancel</Button>
      <Button type="button" variant="destructive" loading={submitting} loadingLabel="Deleting..." onClick={submit} data-testid="quotation-delete-confirm" data-action-category="destructive">
        <Trash2 />Delete Quotation
      </Button>
    </>
  );

  return (
    <FlowpiAlertDialog open={open} onClose={close} busy={submitting} eyebrow="Quotation Lifecycle" title="Delete Quotation?" footer={footer} testId="quotation-delete-dialog">
      <div className="space-y-5">
          <div className="flex gap-3 rounded-sm border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 shrink-0" />
            <p>This quotation will leave active History and be preserved as frozen deleted history. It cannot be restored.</p>
          </div>
          <dl className="rounded-sm border border-zinc-200 bg-zinc-50 px-4 py-4 text-sm dark:border-zinc-800 dark:bg-zinc-900/50">
            <dt className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Quotation Number</dt>
            <dd className="mt-1 font-semibold tracking-[0.02em] text-zinc-900 dark:text-zinc-100">{quotation.quotation_number || "-"}</dd>
          </dl>
          <div>
            <label htmlFor="quotation-delete-reason" className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-600 dark:text-zinc-300">Delete Reason</label>
            <textarea
              id="quotation-delete-reason"
              value={reason}
              onChange={(event) => { setReason(event.target.value); setLocalError(""); }}
              disabled={submitting}
              aria-invalid={Boolean(localError)}
              aria-describedby={localError ? "quotation-delete-reason-error" : undefined}
              className="mt-2 min-h-[100px] w-full resize-none rounded-sm border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 disabled:opacity-60 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
              placeholder="Explain why this quotation is being deleted"
            />
            <FieldFeedback id="quotation-delete-reason-error">{localError}</FieldFeedback>
          </div>
          <FormError data-testid="quotation-delete-error">{error}</FormError>
      </div>
    </FlowpiAlertDialog>
  );
}
